import { gql } from '@apollo/client';
import {
  AdornedInput,
  Alert,
  Box,
  Checkbox,
  DatePicker,
  FieldSet,
  FormField,
  FormHelp,
  Icon,
  Label,
  LabeledControl,
  Paragraph,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';
import { IconCalendarFilled, IconLockClosedFilled } from '@zillow/constellation-icons';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import { format } from 'date-fns';
import type React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import type { ConversationList_GetConversationsQuery } from '../../types/GraphqlTypes';
import {
  ConversationList_GetConversationsDocument,
  NotesModal_GetNewStatusLabelsDocument,
  RenterProgressStatus,
  useNotesModal_GetNotesDataQuery,
} from '../../types/GraphqlTypes';
import { EVENT_TRACKING_CATEGORIES } from '../../constants/AnalyticsConstants';
import {
  emitApplicationSentCheckboxClickEvent,
  emitNoteSavedEvent,
  emitScheduleTourEvent,
} from '../../constants/clickstreamEvents/notesModal';
import StringConstants from '../../constants/StringConstants';
import { MAX_OLD_CONVERSATION_DAYS } from '../../constants/ConfigConstants';
import { AppContext } from '../../contexts/AppContextProvider';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import dateTimeUtils from '../../utils/dateTime/dateTimeUtils';
import TimePicker from '../TimePicker';
import { SavedIndicator } from './SavedIndicator';
import {
  emitEditTourDateEvent,
  emitEditTourTimeEvent,
  emitMakeANoteEvent,
} from '../../constants/clickstreamEvents/renterDetailsModal';
import { logApolloError } from '../../utils/errorUtils';
import { logError } from '@zg-rentals/log-error';
import { useAuth } from '@zg-rentals/auth-comet';
import useEventTrackingCategory from '../../hooks/useEventTrackingCategory';
import { labelStyles } from './styles';
import {
  useSatelliteWebLandlordConversationHandler_updateConversationAttributes,
  useSatelliteWebLandlordConversationHandler_updateTourScheduledTime,
  useSatelliteWebLandlordNoteHandler_createOrUpdate,
  useSatelliteWebLandlordNoteHandler_delete,
} from '@zg-rentals/ts-satellite-client-react-query';
import apolloClient from '../../lib/apollo';

const PRIVACY_TEXT = 'Any information you add here is only visible to you.';
const ERROR_MESSAGE = 'Sorry, there was an error. Try refreshing the page.';
const TEXT_AREA_PLACEHOLDER =
  'e.g. Rachel and I spoke on the phone 8/8. She has a roommate and a small, hypoallergenic dog.';
const DATE_PICKER_FORMAT = 'MM/dd/yyyy';
const INVALID_TOUR_MESSAGE = `Invalid "Tour Scheduled" date. Please fix and try again.`;
const NOTE_MAX_LENGTH = 11000;

type ContactsPageNoteUpdate = {
  hasScheduledTour?: boolean;
  hasSentApplication?: boolean;
  note?: string;
  noteLastUpdatedMs?: number;
};

type Props = {
  clientTimeZone: string;
  conversationId: string;
  listingAlias: string;
  onContactsPageConversationNoteUpdate?: (conversationId: string, noteUpdate: ContactsPageNoteUpdate) => void;
  shouldRefetchStatusLabels: boolean;
  resetAllInlineFeedbackChildState?: boolean;
  onResetAllInlineFeedbackChildState?: () => void;
  shouldSaveNoteText?: boolean;
  onHandleNoteSavingComplete?: () => void;
};

const Notepad = ({
  clientTimeZone,
  conversationId,
  listingAlias,
  onContactsPageConversationNoteUpdate,
  shouldRefetchStatusLabels,
  resetAllInlineFeedbackChildState = false,
  onResetAllInlineFeedbackChildState = () => {},
  shouldSaveNoteText = false,
  onHandleNoteSavingComplete = () => {},
}: Props): JSX.Element | null => {
  const { isImpersonator } = useContext(AppContext);
  const { daysBack, folder, isByProperty, readRepliedStatus } = useContext(InboxPageContext);
  const { data: notepadData } = useNotesModal_GetNotesDataQuery({
    variables: {
      conversationId,
      listingAlias,
    },
    onError: (error) => {
      logApolloError(error, {
        details: 'NotesModal - Error fetching data for the notepad',
        conversationId,
        listingAlias,
      });
    },
    fetchPolicy: 'cache-first',
  });

  const { user } = useAuth();
  const eventTrackingCategory = useEventTrackingCategory();

  const notesData = notepadData?.ZRM_getConversationById;
  const hasZrmToursEnabled = notesData?.touring.hasZrmToursEnabled ?? false;
  const applicationAlreadySent = notesData?.applicationAlreadySent || false;
  const applicationMarkedSent = notesData?.attributes?.applicationMarkedSent || false;
  const tourScheduledTimestampMs = notesData?.attributes?.tourScheduledTimestampMs || '';
  const formattedTourScheduledDate = tourScheduledTimestampMs
    ? format(Number(tourScheduledTimestampMs), DATE_PICKER_FORMAT)
    : '';
  const noteDetails = notesData?.note;

  const [inlineFeedbackForApplicationSent, setInlineFeedbackForApplicationSent] = useState(false);
  const [inlineFeedbackForTourScheduled, setInlineFeedbackForTourScheduled] = useState(false);
  const [inlineFeedbackForNoteUpdated, setInlineFeedbackForNoteUpdated] = useState(false);
  const [showAlertMessage, setShowAlertMessage] = useState(false);
  const [customizedAlertMessage, setCustomizedAlertMessage] = useState('');

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [noteText, setNoteText] = useState<string | null | undefined>('');
  const [noteNeedsSaving, setNoteNeedsSaving] = useState(false);

  const resetAllInlineFeedbackState = useCallback(() => {
    setInlineFeedbackForNoteUpdated(false);
    setInlineFeedbackForTourScheduled(false);
    setInlineFeedbackForApplicationSent(false);
    setShowAlertMessage(false);
    setCustomizedAlertMessage('');
  }, []);

  useEffect(() => {
    if (notepadData) {
      setNoteText(noteDetails?.note);
      setIsCheckboxChecked(applicationAlreadySent || applicationMarkedSent || false);
    }
  }, [notepadData, listingAlias, noteDetails?.note, applicationAlreadySent, applicationMarkedSent]);

  useEffect(() => {
    if (resetAllInlineFeedbackChildState) {
      resetAllInlineFeedbackState();
      onResetAllInlineFeedbackChildState();
    }
  }, [resetAllInlineFeedbackChildState, onResetAllInlineFeedbackChildState, resetAllInlineFeedbackState]);

  useEffect(() => {
    const handleSave = async () => {
      await saveNoteText();
      onHandleNoteSavingComplete();
    };

    if (shouldSaveNoteText) {
      handleSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSaveNoteText, onHandleNoteSavingComplete]);

  const { mutateAsync: updateConversationAttributes } =
    useSatelliteWebLandlordConversationHandler_updateConversationAttributes({
      meta: {
        context: {
          api: 'updateConversationAttributes',
          details: 'Notepad - Error updating conversation attributes',
          conversationId,
        },
      },
    });

  const { mutateAsync: updateTourScheduledTime } = useSatelliteWebLandlordConversationHandler_updateTourScheduledTime({
    meta: {
      context: {
        api: 'updateTourScheduledTime',
        details: 'Notepad - Error updating tour scheduled time',
        conversationId,
      },
    },
  });

  const { mutateAsync: deleteNoteReactQuery } = useSatelliteWebLandlordNoteHandler_delete({
    meta: {
      context: {
        api: 'deleteNote',
        details: 'Notepad - Error deleting note',
        conversationId,
      },
    },
  });

  const { mutateAsync: createOrUpdateNoteReactQuery } = useSatelliteWebLandlordNoteHandler_createOrUpdate({
    meta: {
      context: {
        api: 'createOrUpdateNote',
        details: 'Notepad - Error creating or updating note',
        conversationId,
      },
    },
  });

  // TODO: Remove this function once useNotesModal_GetNotesDataQuery and other Apollo
  // queries reading note data are migrated to React Query.
  // Currently needed because we're in a hybrid state: writes use React Query but reads still use Apollo.
  // This keeps Apollo's cache in sync so the UI reflects changes immediately.
  // Components affected: InboxSidebar NoteSection (uses fetchPolicy: 'cache-only').
  const updateApolloNoteCache = (noteValue: string): void => {
    apolloClient.cache.modify({
      id: apolloClient.cache.identify({
        __typename: 'ZRM_Note',
        conversationId,
      }),
      fields: {
        note() {
          return noteValue;
        },
        lastUpdatedMs() {
          return new Date().getTime();
        },
      },
    });
  };

  const saveNoteText = async (): Promise<void> => {
    setNoteNeedsSaving(false);

    if (noteNeedsSaving) {
      if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.LEAD_MANAGEMENT) {
        emitNoteSavedEvent(user, { listingAlias });
      }

      if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.LEAD_MANAGEMENT_PAGE) {
        emitMakeANoteEvent(user);
      }

      try {
        const nowMs = Date.now();
        if (!noteText?.trim()) {
          await deleteNoteReactQuery({
            postData: {
              conversationId,
            },
          });
          setShowAlertMessage(false);
          setInlineFeedbackForNoteUpdated(true);
          updateApolloNoteCache('');
          onContactsPageConversationNoteUpdate?.(conversationId, {
            note: '',
            noteLastUpdatedMs: nowMs,
          });
        } else {
          const savedNote = noteText?.trim() || '';
          await createOrUpdateNoteReactQuery({
            postData: {
              conversationId,
              note: savedNote,
            },
          });
          setShowAlertMessage(false);
          setInlineFeedbackForNoteUpdated(true);
          updateApolloNoteCache(savedNote);
          onContactsPageConversationNoteUpdate?.(conversationId, {
            note: savedNote,
            noteLastUpdatedMs: nowMs,
          });
        }
      } catch (error) {
        setShowAlertMessage(true);
      }
    }
  };

  const handleNoteTextChange = (e: React.FormEvent<HTMLTextAreaElement>): void => {
    const { value } = e.currentTarget;
    setNoteText(value);

    if (!noteNeedsSaving) {
      setNoteNeedsSaving(true);
    }

    if (inlineFeedbackForNoteUpdated) {
      setInlineFeedbackForNoteUpdated(false);
    }
  };

  const handleApplicationSentChange = async (): Promise<void> => {
    const newApplicationMarkedSent = !isCheckboxChecked;
    setIsCheckboxChecked(newApplicationMarkedSent);

    if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.LEAD_MANAGEMENT) {
      emitApplicationSentCheckboxClickEvent(user, { listingAlias });
    }

    try {
      await updateConversationAttributes({
        postData: {
          conversationId,
          isApplicationMarkedSent: newApplicationMarkedSent,
        },
      });

      apolloClient.cache.writeFragment({
        id: apolloClient.cache.identify({ conversationId, __typename: 'ZRM_Conversation' }),
        fragment: gql`
          fragment NoteData on ZRM_Conversation {
            attributes {
              tourScheduledTimestampMs
              __typename
              applicationMarkedSent
            }
          }
        `,
        data: {
          attributes: {
            tourScheduledTimestampMs,
            __typename: 'ZRM_ConversationAttributes',
            applicationMarkedSent: newApplicationMarkedSent,
          },
        },
      });

      apolloClient.cache.modify({
        id: apolloClient.cache.identify({
          __typename: 'ZRM_Note',
          conversationId,
        }),
        fields: {
          lastUpdatedMs() {
            return new Date().getTime();
          },
        },
      });

      setShowAlertMessage(false);
      setInlineFeedbackForApplicationSent(newApplicationMarkedSent);
      onContactsPageConversationNoteUpdate?.(conversationId, {
        hasSentApplication: newApplicationMarkedSent,
      });

      if (shouldRefetchStatusLabels) {
        await apolloClient.query({
          query: NotesModal_GetNewStatusLabelsDocument,
          variables: { conversationId, listingAlias, clientTimeZone },
          fetchPolicy: 'network-only',
        });
      }
    } catch {
      setShowAlertMessage(true);
    }
  };

  const handleDatePickerInputChange = async (date: Date | string): Promise<void> => {
    // DatePicker with dateFormat should return a formatted string, but handle both cases
    const newDateString = typeof date === 'string' ? date : format(date, DATE_PICKER_FORMAT);

    const newTimestampMs = dateTimeUtils.updateTimestampMsWithNewDate({
      newDateString,
      timestampMsToUpdate: tourScheduledTimestampMs,
    });

    if (newTimestampMs === tourScheduledTimestampMs) {
      return;
    }

    if (newTimestampMs === 'NaN') {
      logError({
        error: new Error('Notepad handleDatePickerInputChange error'),
        context: 'NotesModal - User attempted to updateNote() with tourScheduledTimestampMs of "NaN"',
      });
      setShowAlertMessage(true);
      setCustomizedAlertMessage(INVALID_TOUR_MESSAGE);
    }

    if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.LEAD_MANAGEMENT) {
      emitScheduleTourEvent(user, { listingAlias });
    }

    if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.LEAD_MANAGEMENT_PAGE) {
      emitEditTourDateEvent(user);
    }

    try {
      await updateTourScheduledTime({
        postData: {
          conversationId,
          tourScheduledTimeMs: Number(newTimestampMs),
        },
      });

      if (!tourScheduledTimestampMs) {
        apolloClient.cache.evict({
          fieldName: 'ZRM_getLatestConversationsV2',
          args: {
            clientTimeZone,
            folder,
            listingAlias: isByProperty ? listingAlias : null,
            readRepliedStatus,
            renterProgressStatus: RenterProgressStatus.Tours,
          },
        });
        apolloClient.cache.gc();
      }

      apolloClient.cache.writeFragment({
        id: apolloClient.cache.identify({ conversationId, __typename: 'ZRM_Conversation' }),
        fragment: gql`
          fragment NoteData on ZRM_Conversation {
            attributes {
              tourScheduledTimestampMs
              __typename
              applicationMarkedSent
            }
          }
        `,
        data: {
          attributes: {
            tourScheduledTimestampMs: newTimestampMs,
            __typename: 'ZRM_ConversationAttributes',
            applicationMarkedSent,
          },
        },
      });

      apolloClient.cache.modify({
        id: apolloClient.cache.identify({
          __typename: 'ZRM_Note',
          conversationId,
        }),
        fields: {
          lastUpdatedMs() {
            return new Date().getTime();
          },
        },
      });

      setShowAlertMessage(false);
      setCustomizedAlertMessage('');
      setInlineFeedbackForTourScheduled(true);
      onContactsPageConversationNoteUpdate?.(conversationId, { hasScheduledTour: true });
    } catch {
      setShowAlertMessage(true);
    }
  };

  const handleClearTourScheduledTimestampMs = async (): Promise<void> => {
    setInlineFeedbackForTourScheduled(false);
    setShowAlertMessage(false);

    try {
      await updateTourScheduledTime({
        postData: {
          conversationId,
          tourScheduledTimeMs: 0,
        },
      });

      apolloClient.cache.writeFragment({
        id: apolloClient.cache.identify({ conversationId, __typename: 'ZRM_Conversation' }),
        fragment: gql`
          fragment TourScheduledUpdate on ZRM_Conversation {
            attributes {
              tourScheduledTimestampMs
              __typename
              applicationMarkedSent
            }
          }
        `,
        data: {
          attributes: {
            tourScheduledTimestampMs: '',
            __typename: 'ZRM_ConversationAttributes',
            applicationMarkedSent,
          },
        },
      });

      apolloClient.cache.updateQuery<ConversationList_GetConversationsQuery>(
        {
          query: ConversationList_GetConversationsDocument,
          variables: {
            clientTimeZone,
            daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
            folder,
            listingAlias: isByProperty ? listingAlias : null,
            readRepliedStatus,
            renterProgressStatus: RenterProgressStatus.Tours,
            skipMerge: true,
          },
        },
        (data) => {
          if (!data) return;

          const updatedCachedConversationsMarkedAsToured = data.ZRM_getLatestConversationsV2.conversations.filter(
            (conversation) => conversation.conversationId !== conversationId,
          );
          return {
            ZRM_getLatestConversationsV2: {
              ...data.ZRM_getLatestConversationsV2,
              conversations: updatedCachedConversationsMarkedAsToured || [],
            },
          };
        },
      );
      onContactsPageConversationNoteUpdate?.(conversationId, { hasScheduledTour: false });
    } catch {
      setShowAlertMessage(true);
    }
  };

  const handleTimePickerChange = async (tourScheduledTimestampMs: string): Promise<void> => {
    if (eventTrackingCategory === EVENT_TRACKING_CATEGORIES.LEAD_MANAGEMENT_PAGE) {
      emitEditTourTimeEvent(user);
    }

    try {
      await updateTourScheduledTime({
        postData: {
          conversationId,
          tourScheduledTimeMs: Number(tourScheduledTimestampMs),
        },
      });

      apolloClient.cache.writeFragment({
        id: apolloClient.cache.identify({ conversationId, __typename: 'ZRM_Conversation' }),
        fragment: gql`
          fragment TourScheduledUpdate on ZRM_Conversation {
            attributes {
              tourScheduledTimestampMs
              __typename
              applicationMarkedSent
            }
          }
        `,
        data: {
          attributes: {
            tourScheduledTimestampMs,
            __typename: 'ZRM_ConversationAttributes',
            applicationMarkedSent,
          },
        },
      });

      apolloClient.cache.modify({
        id: apolloClient.cache.identify({
          __typename: 'ZRM_Note',
          conversationId,
        }),
        fields: {
          lastUpdatedMs() {
            return new Date().getTime();
          },
        },
      });

      setShowAlertMessage(false);
      setInlineFeedbackForTourScheduled(true);
      onContactsPageConversationNoteUpdate?.(conversationId, { hasScheduledTour: true });
    } catch {
      setShowAlertMessage(true);
    }
  };

  return (
    <>
      {showAlertMessage && (
        <Alert tone="critical" css={{ marginBottom: 'loose' }}>
          {customizedAlertMessage === '' ? ERROR_MESSAGE : customizedAlertMessage}
        </Alert>
      )}

      {/* Tour date and time selector */}
      {!hasZrmToursEnabled && (
        <>
          <TooltipWrapper wrapInTooltip={isImpersonator} tooltipText={StringConstants.TOOLTIP.IMPERSONATION}>
            <FormField css={{ marginBlock: 'tightest' }}>
              <Box
                css={{
                  ...labelStyles,
                  marginBottom: 'tighter',
                }}
              >
                <Label>Tour details</Label>
                {inlineFeedbackForTourScheduled && <SavedIndicator />}
              </Box>

              <DatePicker
                closeModalOnDateSelect
                disabled={isImpersonator}
                onValueChange={(date) => handleDatePickerInputChange(date)}
                dateFormat={DATE_PICKER_FORMAT}
                input=""
                trigger={
                  <AdornedInput
                    input={
                      <AdornedInput.Input readOnly value={formattedTourScheduledDate} css={{ cursor: 'pointer' }} />
                    }
                    endAdornment={
                      <AdornedInput.Adornment>
                        <AdornedInput.Icon css={{ color: 'icon.neutral' }}>
                          <IconCalendarFilled />
                        </AdornedInput.Icon>
                      </AdornedInput.Adornment>
                    }
                    css={{
                      borderTopLeftRadius: 'AdornedInput.md.default',
                      borderBottomLeftRadius: 'AdornedInput.md.default',
                    }}
                  />
                }
              />
            </FormField>
          </TooltipWrapper>

          <FormField css={{ marginBlock: 'tightest' }}>
            <TimePicker
              onTimePickerChange={handleTimePickerChange}
              tourScheduledTimestampMs={tourScheduledTimestampMs}
            />
          </FormField>

          {tourScheduledTimestampMs && (
            <TextButton
              tone="neutral"
              disabled={isImpersonator}
              textStyle="body-sm"
              onClick={handleClearTourScheduledTimestampMs}
            >
              Remove tour
            </TextButton>
          )}
        </>
      )}

      {/* Application status checkbox */}
      <FormField>
        <Box
          css={{
            ...labelStyles,
            marginTop: hasZrmToursEnabled ? 0 : 'loose',
          }}
        >
          <Label>Application status</Label>
          {inlineFeedbackForApplicationSent && <SavedIndicator />}
        </Box>

        {applicationAlreadySent ? (
          <FieldSet>
            <LabeledControl
              disabled={isImpersonator}
              control={<Checkbox checked disabled />}
              label={<Label>Sent to renter using Zillow Applications</Label>}
            />
          </FieldSet>
        ) : (
          <TooltipWrapper wrapInTooltip={isImpersonator} tooltipText={StringConstants.TOOLTIP.IMPERSONATION}>
            <FieldSet>
              <LabeledControl
                disabled={isImpersonator}
                control={<Checkbox checked={isCheckboxChecked} onChange={handleApplicationSentChange} />}
                label={<Label>Sent to renter</Label>}
              />
            </FieldSet>
          </TooltipWrapper>
        )}
      </FormField>

      {/* Notes section */}
      <FormField
        label={
          <Box
            css={{
              ...labelStyles,
              marginBottom: 'tighter',
              marginTop: 'default',
            }}
          >
            <Label>Notes</Label>
            {inlineFeedbackForNoteUpdated && <SavedIndicator />}
          </Box>
        }
        control={
          <TooltipWrapper wrapInTooltip={isImpersonator} tooltipText={StringConstants.TOOLTIP.IMPERSONATION}>
            <Textarea
              disabled={isImpersonator}
              onBlur={saveNoteText}
              onChange={handleNoteTextChange}
              placeholder={TEXT_AREA_PLACEHOLDER}
              rows={5}
              value={noteText || ''}
              maxLength={NOTE_MAX_LENGTH}
              css={{ resize: 'vertical' }}
            />
          </TooltipWrapper>
        }
        description={<FormHelp>{`${(noteText || '').length}/${NOTE_MAX_LENGTH} characters`}</FormHelp>}
      />

      {/* Privacy text */}
      <Box css={{ display: 'flex', alignItems: 'center', marginTop: 'loose', gap: 'tighter' }}>
        <Paragraph textStyle="body-xs">{PRIVACY_TEXT}</Paragraph>
        <Icon css={{ color: 'icon.subtle' }}>
          <IconLockClosedFilled />
        </Icon>
      </Box>
    </>
  );
};

export default Notepad;
